import {
  TeamBulkButtonComponents,
  TeamTableBulkName,
} from "@components/common/faro-table/faro-table-types";
import { BulkChangeCompanyRole } from "@components/table/members/bulk-actions/bulk-change-company-role";
import { BulkDeleteTeams } from "@components/table/teams/bulk-actions/bulk-delete-teams";
import { BulkExportTeams } from "@components/table/teams/bulk-actions/bulk-export-teams";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { SdbTeam } from "@custom-types/teams-types";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useShouldShowBulkAction } from "@hooks/table/use-should-show-bulk-action";
import { useAppSelector } from "@store/store-helper";
import { fetchingTeamsFlagsSelector } from "@store/teams/teams-selector";
import { selectedEntitiesSelector } from "@store/table/table-selector";
import { useMemo } from "react";

interface Props extends BaseCompanyIdProps {
  /** List of teams to process in bulk actions */
  teams: SdbTeam[];
}

/** List all the bulk action components for teams table */
export function TeamsPageBulkActions({
  companyId,
  teams,
}: Props): TeamBulkButtonComponents {
  const selectedEntities = useAppSelector(selectedEntitiesSelector("teams"));
  const { isFetchingTeamDetails } = useAppSelector(fetchingTeamsFlagsSelector);
  const isFetching = isFetchingTeamDetails === "pending";
  const isAnyRowSelected = selectedEntities.length !== 0;
  const { canDeleteTeams, canEditMember } = useHasUserValidRoleCompanyLevel();

  const isAnyIsManaged = selectedEntities.some(
    (team) => team.category.isManaged
  );

  const allowedButtonsBasedOnRowSelection: TeamTableBulkName[] = useMemo(() => {
    const allowedButtons: TeamTableBulkName[] = ["export"];

    if (isAnyRowSelected) {
      if (canEditMember) {
        allowedButtons.push("changeRole");
      }

      if (canDeleteTeams && !isAnyIsManaged) {
        allowedButtons.push("removeTeam");
      }
    }

    return allowedButtons;
  }, [canDeleteTeams, canEditMember, isAnyIsManaged, isAnyRowSelected]);

  const { shouldShowBulkButtons } = useShouldShowBulkAction({
    allowedButtonsBasedOnRowSelection,
  });

  /** Determine whether to show the export button */
  const shouldShowExportButton =
    shouldShowBulkButtons("export") && (isFetching || teams.length > 0);

  return {
    changeRole: shouldShowBulkButtons("changeRole") ? (
      <BulkChangeCompanyRole companyId={companyId} entityType="teams" />
    ) : null,

    removeTeam: shouldShowBulkButtons("removeTeam") ? (
      <BulkDeleteTeams companyId={companyId} />
    ) : null,

    export: shouldShowExportButton ? (
      <BulkExportTeams
        selectedTeams={isAnyRowSelected ? selectedEntities : teams}
        areEntitiesSelected={isAnyRowSelected}
      />
    ) : null,
  };
}
